import React from 'react';
import { Form, Input, Modal, Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { ProjectDetailsDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { BasicFormWrapper, CustomSwitch } from 'container/styled';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { ProjectsApi } from 'api/api';

interface ProjectFormProps {
  visible: boolean;
  editMode: boolean;
  project?: ProjectDetailsDto | null;
  onCancel: () => void;
  onSuccess: (projectId: number) => Promise<void>;
}

const projectsApi = new ProjectsApi();

export const ProjectForm: React.FC<ProjectFormProps> = ({
  visible,
  editMode,
  project,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (project && editMode) {
      form.setFieldsValue({
        id: project.id,
        name: project.name,
        description: project.description,
        isActive: project.isActive,
      });
    }
  }, [project, editMode, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      if (editMode) {
        await projectsApi.apiProjectsIdPut({
          id: values.id,
          updateProjectCommand: values,
        });
      } else {
        await projectsApi.apiProjectsPost({
          createProjectCommand: values,
        });
      }

      openNotificationWithIcon(
        'success',
        t(
          `projects:${editMode ? 'updated' : 'created'}`,
          `Project ${editMode ? 'updated' : 'created'} successfully`
        )
      );
      await onSuccess(values.id);
    } catch (err) {
      openNotificationWithIcon('error', t('projects:error', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t(`projects:${editMode ? 'edit' : 'create'}`, `${editMode ? 'Edit' : 'Create'} Project`)}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <BasicFormWrapper>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label={t('projects:name', 'Project Name')}
            name="name"
            rules={[
              { required: true, message: t('projects:name-required', 'Project name is required') },
              { max: 200, message: t('projects:name-max', 'Project name cannot exceed 200 characters') }
            ]}
          >
            <Input prefix={<FeatherIcon icon="briefcase" size={16} />} />
          </Form.Item>

          <Form.Item
            label={t('projects:description', 'Description')}
            name="description"
            rules={[
              { max: 500, message: t('projects:description-max', 'Description cannot exceed 500 characters') }
            ]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          {editMode && (
            <Form.Item
              label={t('projects:status', 'Status')}
              name="isActive"
              valuePropName="checked"
            >
              <CustomSwitch />
            </Form.Item>
          )}

          <div className="form-actions">
            <Button onClick={onCancel} style={{ marginRight: 8 }}>
              {t('global:cancel', 'Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editMode 
                ? t('global:update', 'Update')
                : t('global:create', 'Create')
              }
            </Button>
          </div>
        </Form>
      </BasicFormWrapper>
    </Modal>
  );
};

export default ProjectForm;